import { ParseXml } from '../../api/converters/parseXml';
import { Logger } from '../../logger/Logger';
import type { CoreStoreType } from '../../store/CoreStore';
import { RestartController } from '../restartController';

import {
  ConnectionStatus,
  type GameCommunicationMessageType,
  type GameCommunicationTimeout,
  type IGameCommunicationConnectionObserver,
  type IGameCommunicationProcessor,
  type IGameCommunicationSender,
} from './types';

const RECONNECT_LIMIT = 5;

export class GameCommunicationController {
  private ws?: WebSocket;
  private pingInterval?: number;
  private reconnectInterval?: number;
  private store?: CoreStoreType;
  private processors: Record<string, IGameCommunicationProcessor> = {};
  private senders: Record<string, IGameCommunicationSender<unknown>> = {};
  private open = false;
  private waitingPong: boolean = false;
  private url: string = '';
  private jsessionId: string = '';
  private tableId: string = '';
  private attempt: number = 0;
  private isGamePaused = false;
  private isInitialized = false;
  private connectionObserver: IGameCommunicationConnectionObserver | undefined;
  private initialServerMessageDelay: number = 0;
  private connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private serverMessageDelay: number = 0;
  private readonly onWebSocketMessage: (msg: MessageEvent) => void;
  private readonly onSocketClose: () => void;
  private processorsQueue: GameCommunicationTimeout[] = [];
  private currentGame: number | null = null;

  constructor({
    processors = [],
    connectionObserver,
  }: {
    processors: IGameCommunicationProcessor[];
    connectionObserver: IGameCommunicationConnectionObserver;
  }) {
    processors.forEach((processor) => this.setProcessor(processor));

    this.setConnectionObserver(connectionObserver);

    this.onWebSocketMessage = this.handleWebSocketMessage.bind(this);
    this.onSocketClose = this.handleSocketClose.bind(this);

    RestartController.addListener('game-communication', () => {
      this.restart();
    });
  }

  public setCredentials = (url: string, jsessionId: string, tableId: string): void => {
    const oldUrl = this.url;
    const oldJsessionId = this.jsessionId;
    const oldTableId = this.tableId;

    this.url = url;
    this.jsessionId = jsessionId;
    this.tableId = tableId;

    if (this.open) {
      if (this.url !== oldUrl || this.jsessionId !== oldJsessionId || this.tableId !== oldTableId) {
        this.ws?.addEventListener('close', () => {
          this.connectSocket();
        });
        const acknowledgeReconnection = (): void => {
          Logger.logEvent('click', 'serverSwitchExecuted', Date.now(), 1, '', false);
          this.ws?.removeEventListener('open', acknowledgeReconnection);
        };
        this.ws?.addEventListener('open', acknowledgeReconnection);
        this.die();
      }
    }
  };

  public restart = (): void => {
    if (!this.ws || !this.open) return;
    this.die();
    this.connectSocket(true);
  };

  public start(): void {
    if (this.open) return;
    this.connectSocket();
  }

  public setServerMessageDelay(delay: number): void {
    this.serverMessageDelay = delay;
    this.initialServerMessageDelay = delay;
  }

  get messageDelay(): number {
    if (this.isInitialized) {
      return this.serverMessageDelay;
    } else {
      return 0;
    }
  }

  public setStore(store: CoreStoreType): void {
    this.store = store;
  }

  public stopStart(isGamePaused: boolean): void {
    if (this.isGamePaused !== isGamePaused) {
      this.isGamePaused = isGamePaused;

      if (isGamePaused) {
        this.die();
        this.setConnectionStatus(ConnectionStatus.DISCONNECTED);
      } else {
        this.connectSocket();
      }
    }
  }

  private handleSocketClose = (): void => {
    if (!this.reconnectInterval) {
      this.reconnectProcedure();
    }
  };

  private connectSocket(reconnect = false): void {
    this.setConnectionStatus(reconnect ? ConnectionStatus.RECONNECTING : ConnectionStatus.CONNECTING);
    if (reconnect) {
      this.serverMessageDelay = 0;
    } else {
      this.serverMessageDelay = this.initialServerMessageDelay;
    }
    if (!this.open) {
      this.open = true;

      performance.mark('websocketConnectionStart');
      this.ws = new WebSocket(
        `${this.url}?${reconnect ? 'reconnect=true&' : ''}JSESSIONID=${this.jsessionId}&tableId=${
          this.tableId
        }&type=json`
      );
      this.ws.addEventListener('error', () => {
        Logger.logFailure('errors', 'gameCommunicationError', 'general', Date.now(), 1, 'error', false);
      });
      this.ws.addEventListener('close', this.onSocketClose);
      this.ws.addEventListener('open', () => {
        clearInterval(this.reconnectInterval);
        this.ws?.addEventListener('message', this.onWebSocketMessage);
        this.reconnectInterval = undefined;
        this.attempt = 0;
      });

      this.pingInterval = window.setInterval(() => {
        if (!this.reconnectInterval && this.waitingPong) {
          // Response taking too long. Trigger reconnection
          this.reconnectProcedure();
        } else {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.waitingPong = true;
            this.ws?.send(`<ping channel="${this.store?.getState().gameState.gameChannel}" time="${Date.now()}"/>`);
          }
        }
      }, 10_000);
    }
  }

  public die(): void {
    if (!this.ws) return;
    clearInterval(this.reconnectInterval);
    this.reconnectInterval = undefined;
    this.ws.removeEventListener('close', this.onSocketClose);
    this.waitingPong = false;
    this.close();
  }

  private setConnectionStatus(value: ConnectionStatus, quiet: boolean = false): void {
    if (this.connectionStatus !== value) {
      this.connectionStatus = value;
      if (!quiet) {
        this.notifyDisconnectionObservers();
      }
    }
  }

  private reconnectProcedure(): void {
    this.waitingPong = false;

    this.reconnectInterval = window.setInterval(() => {
      this.attempt++;
      this.close();
      if (this.attempt > RECONNECT_LIMIT) {
        clearInterval(this.reconnectInterval);
        this.reconnectInterval = undefined;
        this.setConnectionStatus(ConnectionStatus.CONNECTION_LOST);
      } else {
        this.connectSocket(true);
      }
    }, 5_000);
  }

  public setInitialized(): void {
    this.isInitialized = true;
    const websocketConnectionTime = performance.measure('websocketConnectionTime', 'websocketConnectionStart');
    if (websocketConnectionTime) {
      Logger.logDuration(
        'performance',
        'communicationController',
        Date.now(),
        Math.round(websocketConnectionTime.duration),
        '',
        false
      );
    }
  }

  public setProcessor(processor: IGameCommunicationProcessor): void {
    this.processors[processor.messageName] = processor;
  }

  public setSender(sender: IGameCommunicationSender<unknown>): void {
    if (!this.senders[sender.name]) {
      this.senders[sender.name] = sender;
      window.addEventListener(`GameCommunicationSender-${sender.name}`, ((event: CustomEvent<unknown>) => {
        if (this.store && this.ws) {
          sender.send(event.detail, this.store, this.ws.send.bind(this.ws));
        }
      }) as EventListener);
    }
  }

  private handleWebSocketMessage(msg: MessageEvent): void {
    this.chooseProcessMethod(msg.data);
  }

  private chooseProcessMethod(data: any): void {
    if (data.startsWith('<')) {
      this.processXML(data);
    } else {
      this.processJson(data);
    }
  }

  private processJson(data: string): void {
    const parse = JSON.parse(data) as Record<string, unknown>;
    return this.processMessage(parse);
  }

  private processXML(data: string): void {
    const parse = ParseXml.parseString(data) as Record<string, unknown>;
    return this.processMessage(parse);
  }

  private notifyDisconnectionObservers(): void {
    if (this.connectionObserver && this.store) {
      this.connectionObserver?.update(this.connectionStatus, this.store);
    }
    if (this.connectionStatus === ConnectionStatus.RECONNECTING) {
      this.processorsQueue.forEach(({ timeout }) => clearTimeout(timeout));
    }
  }

  public setConnectionObserver(observer: IGameCommunicationConnectionObserver): void {
    this.connectionObserver = observer;
  }

  private connectedProcedure(connectionStatus: ConnectionStatus): void {
    this.attempt = 0;
    this.setConnectionStatus(
      connectionStatus === ConnectionStatus.RECONNECTING ? ConnectionStatus.RECONNECTED : ConnectionStatus.CONNECTED
    );
    this.serverMessageDelay = this.initialServerMessageDelay;
  }

  private close(): void {
    clearInterval(this.pingInterval);

    this.pingInterval = undefined;
    this.ws?.close();
    this.ws?.removeEventListener('message', this.onWebSocketMessage);
    this.open = false;
  }

  private setCurrentGame(gameId: number): void {
    if (this.currentGame === gameId) return;
    if (this.currentGame === null) {
      this.currentGame = gameId;
      return;
    }
    this.processorsQueue.forEach(({ timeout, process, queuedGameId }) => {
      if (queuedGameId === String(gameId)) {
        clearTimeout(timeout);
        process();
      }
    });
    this.processorsQueue = [];
    this.currentGame = gameId;
    if (this.store) {
      this.connectionObserver?.update(ConnectionStatus.NEW_ROUND, this.store);
    }
  }

  private processMessage(parse: Record<string, unknown>): void {
    const messageName: string = Object.keys(parse)[0];
    const message = parse[messageName] as GameCommunicationMessageType;

    if (messageName === 'game') {
      this.setCurrentGame(message.id as number);
    }

    if (!message || !messageName) {
      return;
    }

    if (messageName === 'pong') {
      this.waitingPong = false;
      return;
    }

    message.beforeInit = !this.isInitialized;

    if (!(this.processors[messageName] && this.store && this.ws)) return;
    const messageProcessor = this.processors[messageName];
    const dynamicDelay =
      messageProcessor.delayCorrection !== undefined
        ? this.messageDelay + messageProcessor.delayCorrection
        : this.messageDelay;
    message.delay = messageProcessor?.delay !== undefined ? messageProcessor?.delay : Math.max(dynamicDelay, 0);
    const process = (callback?: () => void): void => {
      if (messageProcessor && this.store && this.ws) {
        messageProcessor.process(message, this.store, this.ws.send.bind(this.ws), {
          connectionStatus: this.connectionStatus,
          connectionProcedure: this.connectedProcedure.bind(this, this.connectionStatus),
        });
      }
      if (callback) {
        callback();
      }
    };
    this.processorsQueue.push({
      messageName,
      seq: message.seq,
      queuedGameId: this.store.getState().gameState.gameId,
      timeout: setTimeout(process, message.delay, () => {
        this.processorsQueue.splice(
          this.processorsQueue.findIndex(({ seq }) => seq === message.seq),
          1
        );
      }),
      process: process,
    });
  }
}
